'use client';
import React, { useEffect, useState, useRef } from 'react';
import { TKeyValuePair } from '@/types';
import ReactDataGrid, {
  CalculatedColumn,
  RenderSortStatusProps,
  DataGridHandle,
  CellSelectArgs,
  CellClickArgs,
  RowsChangeData,
} from 'react-data-grid';
import 'react-data-grid/lib/styles.css';

import AutoSizer from 'react-virtualized-auto-sizer';
import * as Styled from './styled';

import { Allotment, LayoutPriority } from 'allotment';
import RowDetails from './RowDetails';
import SchemaDetails from './SchemaDetails';
import Pagination from '@/components/Pagination';
import ErrorBoundary from '@/components/ErrorBoundary';

import useDataframeContext from '../DataframeContext';
import useDataframeLayoutContext from '../DataFrameLayoutContext';
import useDataframeQueryContext from '../DataframeQueryContext';

import ProcessingIcon from '@/ui/ProcessingIcon';

import { SYSTEM_ROW_ID_FIELD_NAME } from '../consts';

function renderSortStatus({ sortDirection, priority }: RenderSortStatusProps) {
  return (
    <>
      {sortDirection !== undefined ? (sortDirection === 'ASC' ? 'A' : 'D') : null}
      <span className={'sortPriorityClassname'}>{priority}</span>
    </>
  );
}

type DataGridProps = React.HTMLProps<HTMLDivElement> & {
  selectedRow?: TKeyValuePair;
  selectedColumn?: CalculatedColumn<unknown, unknown>;
  evaluationTargetField?: string;
};

const SchemaRowDetailsAllotmentWrapper: React.FC<{
  selectedRow?: TKeyValuePair;
  selectedRowIndex: number;
  selectedColumn?: CalculatedColumn<unknown, unknown> | undefined;
  onCellSelect: ({ rowIndex, columnKey }: { rowIndex: number; columnKey: string }) => void;
  children: React.ReactNode;
  onRowIncrement?: () => void;
  onRowDecrement?: () => void;
}> = ({
  selectedRow,
  selectedRowIndex,
  selectedColumn,
  onCellSelect,
  children,
  onRowIncrement,
  onRowDecrement,
  ...rest
}) => {
  const { viewData, error, mode } = useDataframeContext();
  const dataFrame = viewData;

  const { onAllotmentChange, schemaPaneVisible, detailsPaneVisible } = useDataframeLayoutContext();
  const { revalidating } = useDataframeQueryContext();

  return (
    <Styled.DataGrid $revalidating={revalidating} {...rest}>
      <Allotment proportionalLayout={false} onChange={onAllotmentChange}>
        <Allotment.Pane minSize={225} preferredSize="25%" visible={schemaPaneVisible} snap className="leftPane">
          {schemaPaneVisible && (
            <ErrorBoundary
              fallback={<div>Error loading SchemaDetails</div>}
              containerComponent="DataGrid > SchemaDetails"
            >
              <SchemaDetails />
            </ErrorBoundary>
          )}
        </Allotment.Pane>
        <Allotment.Pane preferredSize="75%" priority={LayoutPriority.High} className="dataPane">
          <ErrorBoundary fallback={<div>Error loading DataGrid</div>} containerComponent="DataGrid > GridPane">
            <Styled.GridPane>{children}</Styled.GridPane>
          </ErrorBoundary>
        </Allotment.Pane>
        <Allotment.Pane minSize={300} preferredSize="20%" visible={detailsPaneVisible} snap className="rightPane">
          {detailsPaneVisible && (
            <ErrorBoundary fallback={<div>Error loading RowDetails</div>} containerComponent="DataGrid > RowDetails">
              <RowDetails
                schema={dataFrame?.schema}
                selectedRow={selectedRow}
                selectedRowIndex={selectedRowIndex}
                selectedColumn={selectedColumn}
                onCellSelect={onCellSelect}
                onRowIncrement={onRowIncrement}
                onRowDecrement={onRowDecrement}
              />
            </ErrorBoundary>
          )}
        </Allotment.Pane>
      </Allotment>
    </Styled.DataGrid>
  );
};

const PaginationControl: React.FC<{}> = () => {
  const { pagination, mode, gridRef } = useDataframeContext();
  const { onPageChange } = useDataframeQueryContext();
  const showPagination = pagination?.total_pages && pagination?.total_pages > 1;
  if (mode === 'sample') {
    return null;
  }

  const scrollToFirstRow = () => gridRef?.current?.scrollToCell({ rowIdx: 0 });

  return (
    showPagination && (
      <div style={{ textAlign: 'center', padding: 5 }}>
        <Pagination compact onClick={(pageNum) => onPageChange(pageNum, scrollToFirstRow())} pagination={pagination} />
      </div>
    )
  );
};

const DataGridControl: React.FC<{
  onCellSelect: (args: CellSelectArgs<any, unknown>) => void;
  focusedRowIndex: number | undefined;
  setFocusedRowIndex: (focusedRowIndex: number | undefined) => void;
  evaluationTargetField?: string;
}> = ({ onCellSelect, focusedRowIndex, setFocusedRowIndex, evaluationTargetField }) => {
  const { rows, columns, updateRow, addBlankRow, addedRowId, setAddedRowId, gridRef, setGridRef } =
    useDataframeContext();
  const { sortColumns, onSortChange } = useDataframeQueryContext();

  const { detailsPaneVisible, setDetailsPaneVisible, openDetailsOnCellFocus, setOpenDetailsOnCellFocus } =
    useDataframeLayoutContext();

  const forceOpenDetailsPane = () => {
    if (!detailsPaneVisible) {
      setDetailsPaneVisible(true);
    }
  };

  const maybeOpenDetailsPane = () => {
    if (openDetailsOnCellFocus && !detailsPaneVisible) {
      // Limit this to only happenning once - otherwise it will open the details pane on every cell focus
      // even if the user is has deliberately closed it
      setOpenDetailsOnCellFocus(false);
      setDetailsPaneVisible(true);
    }
  };

  // Must be var not a useState, to avoid a race condition when navigating bewteen cells in a row
  let activeRow: TKeyValuePair | undefined = undefined;

  // wrapping this in a useEffect to ensure that the gridRef is set when the component is mounted
  // prevents state issues with the DataframeContext
  const componentGridRef = useRef<DataGridHandle>(null);
  useEffect(() => {
    setGridRef(componentGridRef);
  }, [setGridRef, componentGridRef]);

  const onRowChanged = (rows: TKeyValuePair[], { column, indexes }: RowsChangeData<any, unknown>) => {
    const affectedRows = indexes.map((index) => rows[index]);

    affectedRows.forEach((row) => {
      // NOTE: this follows the happy path, where the row is updated successfully
      // Unsure what to do here if the row update fails
      updateRow(row);
      activeRow = row;
      onCellSelect({ rowIdx: indexes[0], row, column });
    });
  };

  // Scroll to the newly added row and set focus on the first cell
  useEffect(() => {
    const addedRow = rows.find((row: TKeyValuePair) => row[SYSTEM_ROW_ID_FIELD_NAME] === addedRowId);
    if (gridRef?.current && addedRow) {
      const rowIdx = rows.findIndex((row: TKeyValuePair) => row[SYSTEM_ROW_ID_FIELD_NAME] === addedRowId);
      gridRef.current!.scrollToCell({ rowIdx });
      gridRef.current!.selectCell({ rowIdx, idx: 1 });
      setAddedRowId(undefined);
    }
  }, [addedRowId, rows, setAddedRowId, gridRef]);

  const onCellDoubleClick = (args: CellClickArgs<any, unknown>) => {
    forceOpenDetailsPane();
  };

  const onCellNavigate = (args: CellSelectArgs<any, unknown>) => {
    const isHeaderRow = args.rowIdx === -1;
    if (isHeaderRow) {
      return;
    }
    maybeOpenDetailsPane();
    // This logic is required in order to ensure that any modifications to the row are
    // reflected in the details pane when navigating to another cell

    const { rowIdx, column } = args;
    if (focusedRowIndex === rowIdx && activeRow) {
      onCellSelect({ rowIdx, row: activeRow, column });
    } else {
      setFocusedRowIndex(rowIdx);
      activeRow = undefined;
      onCellSelect(args);
    }
  };

  // HACK: This is a hack to enable showing the "waiting" state in an evaluation
  // type TEvalStatus = 'waiting' | 'error' | 'success';
  const processedRows = !!evaluationTargetField
    ? rows.map((row: TKeyValuePair) => {
        const evalStatus = `__oxen_eval_status_${row['_oxen_eval_status'] || 'waiting'}`;
        // console.log('evalStatus', evalStatus);
        if (!row[evaluationTargetField]) {
          row[evaluationTargetField] = evalStatus;
          return row;
        }
        return row;
      })
    : rows;

  return (
    <ReactDataGrid
      className="rdg-light" // HACK: this is a hack to force light theme
      defaultColumnOptions={{
        sortable: true,
        resizable: true,
      }}
      ref={gridRef}
      rowHeight={36}
      onSortColumnsChange={onSortChange}
      onRowsChange={onRowChanged}
      onSelectedCellChange={onCellNavigate}
      onCellDoubleClick={onCellDoubleClick}
      sortColumns={sortColumns}
      style={{ blockSize: '100%', backgroundColor: 'transparent' }}
      columns={columns}
      renderers={{ renderSortStatus }}
      rows={processedRows as any[]}
    />
  );
};

const DataGrid: React.FC<DataGridProps & { detailsPaneVisible?: boolean }> = ({
  detailsPaneVisible = true,
  evaluationTargetField,
  ...rest
}) => {
  const [selectedRow, setSelectedRow] = useState<TKeyValuePair | undefined>(undefined);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | undefined>(undefined);
  const [focusedRowIndex, setFocusedRowIndex] = useState<number | undefined>(undefined);
  const [selectedColumn, setSelectedColumn] = useState<CalculatedColumn<unknown, unknown> | undefined>(undefined);
  const { rows, columns, gridRef } = useDataframeContext();

  const onCellSelect = (args: CellSelectArgs<any, unknown>) => {
    const { row, column, rowIdx } = args;
    // console.log('onCellSelect', args);
    setSelectedRow(row);
    setSelectedRowIndex(rowIdx);
    setSelectedColumn(column);
  };

  const onDetailsPaneCellSelect = ({ rowIndex, columnKey }: { rowIndex: number; columnKey: string }) => {
    setSelectedRowIndex(rowIndex);
    // console.log('onDetailsPaneCellSelect', rowIndex, columnKey);
    const column = columns.find((column: any) => column.key === columnKey);
    const columnIndex = columns.findIndex((column: any) => column.key === columnKey);
    gridRef.current!.scrollToCell({ idx: columnIndex, rowIdx: rowIndex });
    // TODO: Selecting a cell in the grid grabs focus away from the details pane, making editing the cell in the details pane janky
    // HACK: Only select the cell if the column is not editable
    if (!column.editable) {
      gridRef.current!.selectCell({ rowIdx: rowIndex, idx: columnIndex });
    }
  };

  const columnIndex = columns.findIndex((column: any) => column.key === selectedColumn?.key);

  useEffect(() => {
    if (gridRef?.current) {
      // console.log('columnIndex', selectedRowIndex, columnIndex);
      gridRef.current!.selectCell({ rowIdx: selectedRowIndex, idx: columnIndex });
    }
  }, [selectedRowIndex, columnIndex]);

  const onRowIncrement = () => {
    // console.log('onRowIncrement', selectedRowIndex);
    // if (selectedRowIndex) {
    const newRowIndex = (selectedRowIndex || 0) + 1;
    setSelectedRow(rows[newRowIndex]);
    setSelectedRowIndex(newRowIndex);
    // }
  };

  const onRowDecrement = () => {
    console.log('onRowDecrement', selectedRowIndex);
    // if (selectedRowIndex) {
    const newRowIndex = (selectedRowIndex || 1) - 1;
    setSelectedRow(rows[newRowIndex]);
    setSelectedRowIndex(newRowIndex);
    // }
  };

  return (
    <AutoSizer defaultHeight={300}>
      {({ height, width }: { height: number; width: number }) => {
        return (
          <SchemaRowDetailsAllotmentWrapper
            style={{ height: height, width: width }}
            {...rest}
            selectedRowIndex={selectedRowIndex || 0}
            selectedRow={selectedRow}
            selectedColumn={selectedColumn}
            onCellSelect={onDetailsPaneCellSelect}
            onRowIncrement={onRowIncrement}
            onRowDecrement={onRowDecrement}
          >
            <DataGridControl
              onCellSelect={onCellSelect}
              evaluationTargetField={evaluationTargetField}
              focusedRowIndex={focusedRowIndex}
              setFocusedRowIndex={setFocusedRowIndex}
            />
            <PaginationControl />
          </SchemaRowDetailsAllotmentWrapper>
        );
      }}
    </AutoSizer>
  );
};

export default DataGrid;
